import { mediaQuery } from '@noths/polaris-client-styles';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { borderRadius, colors, spacing, tint } = themeVariables;

// Layout breakpoint widths (px) the hero switches at. The design system is deliberately
// layout-agnostic and ships no breakpoint tokens, so the carousel owns the widths it responds to.
const BREAKPOINT_M = 768;
const BREAKPOINT_XL = 1440;

/*
 * The hero is full-bleed and each slide is narrower than the viewport so the neighbouring slides
 * peek in, split evenly either side. These two are a pair: the offset must be half of what the slide
 * leaves over, or the track no longer rests with a slide centred. The progress bar uses the offset to
 * line up with the active slide rather than the viewport.
 */
const SLIDE_WIDTH = '86%';
const SLIDE_PEEK_OFFSET = '7%';

/**
 * Pins an absolutely positioned overlay to the active slide rather than the viewport, which is wider
 * because the neighbouring slides peek in. Kept here with the slide widths it has to agree with: from
 * XL up the slides are full width and there is no peek, so the inset has to disappear with it.
 */
export const slideInset = css`
  left: ${SLIDE_PEEK_OFFSET};
  right: ${SLIDE_PEEK_OFFSET};

  ${mediaQuery(
    { min: BREAKPOINT_XL },
    css`
      left: 0;
      right: 0;
    `,
  )};
`;

export const singleItem = css`
  width: 100%;
`;

export const carousel = css`
  position: relative;
  width: 100%;
`;

export const viewport = css`
  overflow: hidden;
  position: relative;
`;

export const container = css`
  display: flex;
  /*
   * Pre-centre the first slide for the server-rendered / pre-hydration frame so it matches where
   * Embla rests it once initialised (align: 'center'). Shifting the track right by the peek offset
   * centres slide 1. Embla replaces this with its own inline transform on init (inline wins), so the
   * image doesn't jump on hydration; only the left-hand looped clone fills in afterwards.
   */
  transform: translate3d(${SLIDE_PEEK_OFFSET}, 0, 0);

  ${mediaQuery(
    { min: BREAKPOINT_XL },
    css`
      transform: translate3d(0, 0, 0);
    `,
  )};
`;

export const slide = css`
  flex: 0 0 ${SLIDE_WIDTH};
  min-width: 0;
  margin-right: var(${spacing.xxs});

  ${mediaQuery(
    { min: BREAKPOINT_XL },
    css`
      flex: 0 0 100%;
      min-width: 0;
      margin-right: var(${spacing.xxs});
    `,
  )};
`;

/*
 * The carousel root, matched by the attribute it already carries for assistive technology, so the
 * arrows can respond to it being hovered without a styled-component class to hang the selector on.
 */
const CAROUSEL_SELECTOR = "[aria-roledescription='carousel']";

/*
 * Distance from the active slide's edge to the arrow. One number for design to adjust. It is measured
 * from the slide rather than the viewport because the peek scales with the viewport: a fixed distance
 * from the viewport edge put the arrow over the gap and the neighbouring slide on wider screens.
 */
const CONTROL_INSET = '24px';
const CONTROL_SIZE = '44px';

/*
 * The arrows are the carousel's own button rather than the design-system IconButton, which has a
 * single transparent appearance and no variant to change it. A transparent arrow disappears into the
 * hero imagery, so these are a solid surface circle. Hover, focus and sizing follow the design
 * system's own solid round button (FavouriteButton) so the two stay consistent, and every value
 * comes from the theme rather than being hard-coded.
 */
export const control = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  width: ${CONTROL_SIZE};
  height: ${CONTROL_SIZE};
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: var(${borderRadius.full.desktop});
  background-color: var(${colors.surface.default});
  color: var(${colors.content.default});
  cursor: pointer;
  /* Separates the circle from a pale image, where a white edge alone would not read. */
  box-shadow: 0 0 4px 0 color-mix(in srgb, var(${colors.content.primary}) 10%, transparent);
  /*
   * Hidden unless the media query below reveals it. Safe as display: none now that the autoplay
   * clock lives in the progress bar, which shows at every screen size: a display: none subtree
   * suspends CSS animations, so this could not hide the clock. It also keeps the arrows out of the
   * tab order wherever they are never shown.
   */
  display: none;
  opacity: 0;
  pointer-events: none;

  &:hover {
    background-color: color-mix(
      in srgb,
      var(${colors.content.default}) var(${tint.hover}),
      var(${colors.surface.default}) calc(100% - var(${tint.hover}))
    );
  }

  &:focus-visible {
    outline: 2px solid var(${colors.stroke.focus});
    outline-offset: 2px;
  }

  /*
   * Only on screens from M up with a mouse or trackpad. Touch devices, tablets included, get no
   * arrows: they swipe, and an arrow that is always on show sits over the slide heading. Where there
   * is a pointer the arrows stay faded out until the carousel is hovered, or something inside it has
   * keyboard focus, so they only cover the heading while someone is reaching for them. The focus
   * case keeps them usable from the keyboard: tabbing to an arrow reveals it.
   */
  @media only screen and (min-width: ${BREAKPOINT_M}px) and (hover: hover) and (pointer: fine) {
    display: flex;
    transition: opacity 200ms ease-in-out;

    ${CAROUSEL_SELECTOR}:hover &,
    ${CAROUSEL_SELECTOR}:has(:focus-visible) & {
      opacity: 1;
      pointer-events: auto;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/*
 * Offset by the peek so the arrows sit inside the active slide, following its edge as the viewport
 * changes. From XL up the slide is full width with no peek, so only the inset remains. Mirrors
 * slideInset, which does the same for the progress bar.
 */
export const controlPrevious = css`
  left: calc(${SLIDE_PEEK_OFFSET} + ${CONTROL_INSET});

  ${mediaQuery(
    { min: BREAKPOINT_XL },
    css`
      left: ${CONTROL_INSET};
    `,
  )};
`;

export const controlNext = css`
  right: calc(${SLIDE_PEEK_OFFSET} + ${CONTROL_INSET});

  ${mediaQuery(
    { min: BREAKPOINT_XL },
    css`
      right: ${CONTROL_INSET};
    `,
  )};
`;
